import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface WeatherData {
  temperature: number;
  condition: string;
  humidity: number;
  windSpeed: number;
  visibility: number;
  location: string;
  country: string;
  icon: string;
}

interface WeatherConfig {
  temperatureUnit: 'celsius' | 'fahrenheit';
  showHumidity: boolean;
  showWindSpeed: boolean;
  showVisibility: boolean;
  location: string;
  theme: 'dark' | 'light';
  unitSystem: 'metric' | 'imperial';
}

interface WeatherContextType {
  weatherData: WeatherData | null;
  config: WeatherConfig;
  loading: boolean;
  updateWeatherData: (data: WeatherData) => void;
  updateConfig: (config: WeatherConfig) => void;
  fetchWeather: (location: string) => Promise<void>;
}

const WeatherContext = createContext<WeatherContextType | undefined>(undefined);

export const useWeather = () => {
  const context = useContext(WeatherContext);
  if (context === undefined) {
    throw new Error('useWeather must be used within a WeatherProvider');
  }
  return context;
};

export const WeatherProvider = ({ children }: { children: ReactNode }) => {
  const [weatherData, setWeatherData] = useState<WeatherData | null>(null);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState<WeatherConfig>({
    temperatureUnit: 'fahrenheit',
    showHumidity: true,
    showWindSpeed: true,
    showVisibility: true,
    location: 'auto',
    theme: 'dark',
    unitSystem: 'imperial'
  });

  const convertTemperature = (temp: number, unit: 'celsius' | 'fahrenheit') => {
    if (unit === 'fahrenheit') {
      return Math.round((temp * 9/5) + 32);
    }
    return temp;
  };

  const fetchWeather = async (searchLocation: string) => {
    try {
      setLoading(true);
      let locationQuery = searchLocation;
      
      if (searchLocation === 'auto') {
        // For auto-detection, try browser geolocation first
        if (navigator.geolocation) {
          try {
            const position = await new Promise<GeolocationPosition>((resolve, reject) => {
              navigator.geolocation.getCurrentPosition(resolve, reject, {
                timeout: 10000,
                enableHighAccuracy: false
              });
            });
            
            const { latitude, longitude } = position.coords;
            locationQuery = `${latitude},${longitude}`;
          } catch (geoError) {
            console.warn('Geolocation failed, using fallback:', geoError);
            locationQuery = 'London, UK'; // Fallback location
          }
        } else {
          locationQuery = 'London, UK'; // Fallback if geolocation not supported
        }
      }
      
      const { data, error } = await supabase.functions.invoke('weather', {
        body: { 
          action: 'fetchWeather', 
          location: locationQuery 
        }
      });

      if (error) throw error;
      
      setWeatherData({
        temperature: Math.round(data.temperature),
        condition: data.condition,
        humidity: data.humidity,
        windSpeed: Math.round(data.windSpeed || 0),
        visibility: Math.round(data.visibility || 0),
        location: data.location,
        country: data.country,
        icon: data.icon
      });
    } catch (error) {
      console.error('Failed to fetch weather:', error);
      // Keep user's chosen location even on failure to avoid confusion
      const fallbackLocation = searchLocation === 'auto' ? 'Current Location' : searchLocation;
      setWeatherData({
        temperature: 22,
        condition: 'Partly Cloudy',
        humidity: 65,
        windSpeed: 12,
        visibility: 10,
        location: fallbackLocation,
        country: '',
        icon: ''
      });
    } finally {
      setLoading(false);
    }
  };

  const updateWeatherData = (data: WeatherData) => {
    setWeatherData(data);
  };

  const updateConfig = (newConfig: WeatherConfig) => {
    setConfig(newConfig);
  };

  // Initial fetch
  useEffect(() => {
    fetchWeather(config.location);
  }, []);

  const value = {
    weatherData,
    config,
    loading,
    updateWeatherData,
    updateConfig,
    fetchWeather
  };

  return (
    <WeatherContext.Provider value={value}>
      {children}
    </WeatherContext.Provider>
  );
};
